// Shell-UI context for the BARK SPA. (BAR-40 route-tree overhaul)
//
// Layout + cross-view chrome state that lives in MainApplicationUI today and
// must survive the move into the persistent <ShellLayout> (since
// MainApplicationUI is decomposed away). Provided INSIDE ShellLayout, below
// the auth gate. Split State (volatile: widths, resize drag, panel-open
// flags) from Actions (stable: navigateToView + setters) so a 60fps canvas
// resize never re-renders the sidebar.
//
// `navigateToView` is the single navigation chokepoint — it wraps
// routes.ts's navStateToPath + the idempotency guard, so every navigation in
// every extracted view funnels through one canonicalizing writer.
//
// Stage 0: contracts + typed hooks only; thin value pass-through provider.
import React, { createContext, useContext } from 'react';
import { MainView, ProjectType, ProjectsFilter } from '../router/routes';
import type { TopicGroup } from '../components/landing/BuildShowcase';

export interface NavigateToViewOptions {
  projectId?: string;
  projectsFilter?: ProjectsFilter;
  newProjectType?: ProjectType;
  quickQuery?: string;
  replace?: boolean;
}

export interface ShellUiState {
  canvasWidthPercent: number;
  isChatHidden: boolean;
  sidebarWidth: number;
  chatPanelWidth: number;
  isSidebarHovered: boolean;
  isAccountMenuOpen: boolean;
  isResizingCanvas: boolean;
  showShareDialog: boolean;
  // BAR-135 D11: optional `imp` flag marks an impersonated collaborator.
  onlineUsers: Array<{ user_id: string; email: string; imp?: boolean }>;
  // Report-view panels shared between WorkspaceRoute and the findings panel
  isReportFindingsPanelOpen: boolean;
  selectedReportFindingId: string | null;
  selectedReportOnlineSupportId: string | null;
  isReportDetailOpen: boolean;
  reportSplitPercent: number;
  reportListWidth: number;
  // Workspace graph source panel
  isSourcePanelOpen: boolean;
  sourcePanelContent: unknown | null;
  highlightedNodeId: string | null;
  reLayoutTrigger: number;
}

export interface ShellUiActions {
  /** The single navigation chokepoint (wraps navStateToPath + idempotency). */
  navigateToView: (view: MainView, opts?: NavigateToViewOptions) => void;
  setIsChatHidden: (v: boolean) => void;
  setIsSidebarHovered: (v: boolean) => void;
  setIsAccountMenuOpen: (v: boolean) => void;
  setShowShareDialog: (v: boolean) => void;
  setIsReportFindingsPanelOpen: (v: boolean) => void;
  setSelectedReportFindingId: (v: string | null) => void;
  setSelectedReportOnlineSupportId: (v: string | null) => void;
  setIsReportDetailOpen: (v: boolean) => void;
  setIsSourcePanelOpen: (v: boolean) => void;
  setSourcePanelContent: (v: any | null) => void;
  setHighlightedNodeId: (v: string | null) => void;
  /** Reset the report/evidence panels on project switch (BAR-53). */
  resetMainAppLocalPanels: () => void;
  onOpenWorkspaceDetails?: () => void;
  onOpenChangePassword?: () => void;
  /** Opens the shared "Unlock <module>" teaser dialog (BAR-538 teaser
   *  cards; same dialog the sidebar Reporting item opens). Required: the
   *  teaser cards render dead-looking but inert without it, so providers
   *  must supply it (App always does). */
  onOpenUnlockModule: (label: string) => void;
  // S4: MainApplicationUI-owned orchestration handlers that the extracted view
  // routes invoke. They mutate MainApplicationUI-local state (the chat pre-seed
  // GeminiChatArea reads on mount; the box-draft flow), so MainApplicationUI —
  // not App — provides them, keeping the prefill setters bound to the SAME
  // state GeminiChatArea consumes (preserving the order-sensitive seed
  // contract: setPrefill → setAutoSend → navigate, all before the chat mounts).
  setPrefillChatInput: (v: string | null) => void;
  setAutoSendPrefill: (v: boolean) => void;
  beginDraft: (topic: TopicGroup) => void;
  /** Enter a topic-less blank draft of the given type and route to the
   *  matching empty workspace (research → /research/new, build → /build/new).
   *  Used by the sidebar entries and the Project Library empty-state CTAs. */
  beginBlankDraft: (type: 'research' | 'build') => void;
  handleSelectProject: (projectId: string) => Promise<void> | void;
}

const ShellUiStateContext = createContext<ShellUiState | undefined>(undefined);
const ShellUiActionsContext = createContext<ShellUiActions | undefined>(undefined);

export function ShellUiProvider({
  state,
  actions,
  children,
}: {
  state: ShellUiState;
  actions: ShellUiActions;
  children: React.ReactNode;
}) {
  return (
    <ShellUiActionsContext.Provider value={actions}>
      <ShellUiStateContext.Provider value={state}>{children}</ShellUiStateContext.Provider>
    </ShellUiActionsContext.Provider>
  );
}

export function useShellUi(): ShellUiState {
  const ctx = useContext(ShellUiStateContext);
  if (ctx === undefined) throw new Error('useShellUi must be used within <ShellUiProvider>');
  return ctx;
}

export function useShellUiActions(): ShellUiActions {
  const ctx = useContext(ShellUiActionsContext);
  if (ctx === undefined) throw new Error('useShellUiActions must be used within <ShellUiProvider>');
  return ctx;
}
